import React from 'react';
import {
  TelemetryPanelConfig,
  TelemetryUnitSystem,
  TelemetryViewportState,
  TelemetryLayoutMode,
} from '../types/telemetry-dashboard.types';
import {
  getTelemetrySubColor,
  getTelemetryVizLabel,
  isTelemetryChartViz,
} from '../utils/telemetry-color.utils';
import {
  streamTableRows,
  transposedTable,
  resolveTableColumns,
  tableColLabel,
  fmtClock,
  getBarCategoryCount,
} from '../utils/telemetry-helpers.utils';
import { TelemetryCardKpi } from './TelemetryCardKpi';
import '../styles/telemetry-dashboard-panel.css';

export interface TelemetryDashboardPanelProps {
  panel: TelemetryPanelConfig;
  timeframe?: number;
  precision?: number;
  units?: TelemetryUnitSystem;
  viewport?: TelemetryViewportState;
  viewportLabel?: string;
  isPaused?: boolean;
  layoutMode?: TelemetryLayoutMode;
  showDragHandle?: boolean;
  showResizeHandles?: boolean;
  showVizDropdown?: boolean;
  showExpand?: boolean;
  showMenu?: boolean;
  onDragDown?: (e: React.PointerEvent) => void;
  onResizeDown?: (dir: string, e: React.PointerEvent) => void;
  onExpand?: (panel: TelemetryPanelConfig) => void;
  onRemove?: (id: string) => void;
  onVizClick?: (e: React.MouseEvent) => void;
  renderCustomHeader?: (panel: TelemetryPanelConfig) => React.ReactNode;
  renderCustomFooter?: (panel: TelemetryPanelConfig) => React.ReactNode;
  chartWrapRef?: (el: HTMLElement | null) => void;
}

export const TelemetryDashboardPanel: React.FC<TelemetryDashboardPanelProps> = ({
  panel,
  timeframe = 5,
  precision = 2,
  units = {},
  viewport = { spanMs: 300000, offsetMs: 0 },
  layoutMode = 'grid',
  showDragHandle = true,
  showResizeHandles = true,
  showVizDropdown = true,
  showExpand = true,
  showMenu = true,
  onDragDown,
  onResizeDown,
  onExpand,
  onRemove,
  onVizClick,
  renderCustomHeader,
  renderCustomFooter,
  chartWrapRef,
}) => {
  const dirs = layoutMode === 'list' ? ['s'] : ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
  const firstParam = panel.parameters?.[0];
  const subColor = firstParam ? getTelemetrySubColor(firstParam.subsystem) : getTelemetrySubColor('');
  const vizMode = panel.graphMode;
  const resolvedCols = vizMode === 'gauge' ? resolveTableColumns(panel as any) : [];
  const totalGauges = resolvedCols.length;
  const isMultiGauge = vizMode === 'gauge' && totalGauges > 1;
  const gaugeGridCols = totalGauges <= 3 ? totalGauges : (totalGauges === 4 ? 2 : 3);
  const gaugeRowCount = Math.ceil(totalGauges / gaugeGridCols);
  const multiGaugeCanvasHeight = Math.max(gaugeRowCount * 170, 180);

  // Horizontal Bar with many categories -> vertical scroll with fixed bar row height
  const hbarCategoryCount = vizMode === 'hbar' ? getBarCategoryCount(panel) : 0;
  const isScrollableHBar = vizMode === 'hbar' && hbarCategoryCount > 6;
  const hbarCanvasHeight = isScrollableHBar ? hbarCategoryCount * 28 + 48 : 0;

  const shouldScrollY = isMultiGauge || isScrollableHBar;
  const canvasMinHeight = isMultiGauge
    ? `${multiGaugeCanvasHeight}px`
    : isScrollableHBar
      ? `${hbarCanvasHeight}px`
      : undefined;

  return (
    <div
      id={`telemetry-panel-${panel.id}`}
      className={`telemetry-panel-card telemetry-panel-card--${layoutMode}`}
    >
      {/* Resize handles */}
      {showResizeHandles && onResizeDown && (
        dirs.map((dir) => (
          <div
            key={dir}
            className={`telemetry-panel-rz telemetry-panel-rz--${dir}`}
            onPointerDown={(e) => onResizeDown(dir, e)}
          />
        ))
      )}

      {/* HEADER SECTION */}
      {renderCustomHeader ? (
        renderCustomHeader(panel)
      ) : (
        <div
          className="telemetry-panel-header"
          onPointerDown={showDragHandle && onDragDown ? onDragDown : undefined}
          style={{ cursor: showDragHandle && onDragDown ? 'grab' : 'default' }}
        >
          {/* <span className="telemetry-panel-sub-dot" style={{ background: subColor }} /> */}
          <span className="telemetry-panel-title">{panel.name || firstParam?.label || firstParam?.paramName || firstParam?.paramId || 'Panel'}</span>

          <span className="telemetry-panel-header-spacer" />

          {/* Viz mode dropdown button */}
          {showVizDropdown && onVizClick && (
            <button className="telemetry-panel-viz-btn" onClick={onVizClick} title="Change Visualization">
              <span>{getTelemetryVizLabel(vizMode)}</span>
              <svg width="8" height="8" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="#7B7B7B" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          )}

          {/* Expand Button */}
          {showExpand && onExpand && isTelemetryChartViz(vizMode) && (
            <button className="telemetry-panel-action-btn" onClick={() => onExpand(panel)} title="Expand">
              ⤢
            </button>
          )}

          {/* Remove Button */}
          {showMenu && onRemove && (
            <button className="telemetry-panel-action-btn" onClick={() => onRemove(panel.id)} title="Remove">
              ✕
            </button>
          )}
        </div>
      )}

      {/* BODY SECTION */}
      <div className="telemetry-panel-body" key={`${panel.id}-${panel.graphMode}`}>
        {/* KPI Card Mode */}
        {vizMode === 'kpi' && (
          <TelemetryCardKpi
            panel={panel}
            timeframe={timeframe}
            precision={precision}
            units={units}
            viewport={viewport}
          />
        )}

        {/* Stream Table Mode */}
        {vizMode === 'ctable' && (
          <div className="telemetry-panel-table-wrap">
            <table className="telemetry-panel-table">
              <thead>
                <tr>
                  <th>Time (UTC)</th>
                  {resolveTableColumns(panel as any).map((col: any, i: number) => (
                    <th key={i}>{tableColLabel(col)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {streamTableRows(panel as any, units as any, precision, timeframe, viewport).map((r: any, ri: number) => (
                  <tr key={ri}>
                    <td className="telemetry-panel-table-time">{r.t}</td>
                    {r.cells.map((cell: any, ci: number) => (
                      <td key={ci}>{cell.v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Single Table Mode */}
        {vizMode === 'table' && (
          <div className="telemetry-panel-table-wrap">
            <table className="telemetry-panel-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  {transposedTable(panel as any, units as any, precision, timeframe, viewport).times.map((t: number, i: number) => (
                    <th key={i}>{fmtClock(t)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transposedTable(panel as any, units as any, precision, timeframe, viewport).paramRows.map((r: any, ri: number) => (
                  <tr key={ri}>
                    <td style={{ fontWeight: 600, color: '#EEE' }}>{tableColLabel(r.c)}</td>
                    {r.cells.map((cell: any, ci: number) => (
                      <td key={ci}>{cell.v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ECharts Canvas Wrapper for Chart Viz Modes */}
        {isTelemetryChartViz(vizMode) && (
          <div
            className="telemetry-panel-chart-canvas-container"
            style={shouldScrollY ? { flex: 1, width: '100%', height: '100%', overflowY: 'auto', overflowX: 'hidden' } : { flex: 1, width: '100%', height: '100%' }}
          >
            <div
              ref={chartWrapRef}
              data-chart-id={panel.id}
              className="telemetry-panel-chart-canvas"
              style={canvasMinHeight ? { width: '100%', height: '100%', minHeight: canvasMinHeight } : undefined}
            />
          </div>
        )}
      </div>

      {/* FOOTER SECTION */}
      {renderCustomFooter && renderCustomFooter(panel)}
    </div>
  );
};
